import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Plus,
  Globe,
  RefreshCw,
  Trash2,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useDeleteSite, useRefreshWorkerCms, useSites, useTestSite, type Site } from '@/hooks/useSites';
import { formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';

export function SitesPage() {
  const { data, isPending } = useSites();
  const refresh = useRefreshWorkerCms();

  // Group workercms sites by base_url to surface a single refresh control per
  // connection — refreshing once re-syncs every site under the same key.
  const wcmsConnections = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of data?.sites ?? []) {
      if (s.platform === 'workercms' && s.workercms_base_url) {
        map.set(s.workercms_base_url, (map.get(s.workercms_base_url) ?? 0) + 1);
      }
    }
    return Array.from(map.entries()).map(([base_url, count]) => ({ base_url, count }));
  }, [data]);

  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);

  const onRefresh = async (base_url: string) => {
    setRefreshMsg(null);
    try {
      const r = await refresh.mutateAsync({ base_url });
      const orphanNote = r.orphans.length > 0 ? ` · ${r.orphans.length} site uzakta yok (sil?)` : '';
      setRefreshMsg(`${base_url}: ${r.added} eklendi, ${r.updated} güncellendi, ${r.total_remote} toplam${orphanNote}`);
    } catch (err) {
      setRefreshMsg(err instanceof Error ? err.message : 'Yenileme başarısız');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold">Siteler</h1>
          <p className="text-muted-foreground text-sm">
            İçerik dispatch edebileceğin WordPress ve WorkerCms siteleri.
          </p>
        </div>
        <Button asChild>
          <Link to="/sites/new">
            <Plus className="w-4 h-4" /> Yeni Site
          </Link>
        </Button>
      </div>

      {wcmsConnections.length > 0 && (
        <Card>
          <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="font-medium">WorkerCms bağlantıları</span>
              <span className="text-muted-foreground">
                {wcmsConnections.length} bağlantı, {wcmsConnections.reduce((a, c) => a + c.count, 0)} site
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {wcmsConnections.map((c) => (
                <Button
                  key={c.base_url}
                  size="sm"
                  variant="outline"
                  onClick={() => onRefresh(c.base_url)}
                  disabled={refresh.isPending}
                >
                  <RefreshCw className={cn('w-3.5 h-3.5', refresh.isPending && 'animate-spin')} />
                  {new URL(c.base_url).hostname} ({c.count})
                </Button>
              ))}
            </div>
          </CardContent>
          {refreshMsg && (
            <CardContent className="px-3 pb-3 pt-0 text-xs text-muted-foreground">{refreshMsg}</CardContent>
          )}
        </Card>
      )}

      {isPending ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-32 rounded-lg border bg-muted/30 animate-pulse" />
          ))}
        </div>
      ) : data?.sites.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {data?.sites.map((s) => <SiteCard key={s.id} site={s} />)}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="py-12 text-center space-y-3">
        <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
          <Globe className="w-6 h-6 text-muted-foreground" />
        </div>
        <div>
          <p className="font-medium">Henüz site eklenmemiş</p>
          <p className="text-sm text-muted-foreground">
            İçerik yayınlamak için önce bir WordPress sitesi ekle.
          </p>
        </div>
        <Button asChild>
          <Link to="/sites/new">
            <Plus className="w-4 h-4" /> İlk Siteyi Ekle
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function SiteCard({ site }: { site: Site }) {
  const [confirming, setConfirming] = useState(false);
  const test = useTestSite(site.id);
  const del = useDeleteSite();

  const onDelete = async () => {
    if (!confirming) {
      setConfirming(true);
      setTimeout(() => setConfirming(false), 3000);
      return;
    }
    await del.mutateAsync(site.id);
  };

  return (
    <Card className="group">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {site.platform === 'workercms' ? (
                <Sparkles className="w-4 h-4 text-primary shrink-0" />
              ) : (
                <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
              )}
              <h3 className="font-medium truncate">{site.name}</h3>
              {site.platform === 'workercms' && (
                <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary font-medium">
                  WorkerCms
                </span>
              )}
            </div>
            <a
              href={site.site_url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 truncate max-w-full"
            >
              <span className="truncate">{site.site_url.replace(/^https?:\/\//, '')}</span>
              <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
          </div>
          <ConnectionBadge status={site.connection_status} />
        </div>

        <div className="flex flex-wrap gap-1 text-xs">
          {site.seo_plugin && site.seo_plugin !== 'none' && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">
              {site.seo_plugin === 'yoast' ? 'Yoast' : 'Rank Math'}
            </span>
          )}
          {site.woocommerce && (
            <span className="rounded-full bg-secondary px-2 py-0.5 text-secondary-foreground">
              WooCommerce
            </span>
          )}
          {site.default_status && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground capitalize">
              {site.default_status}
            </span>
          )}
          {site.tags.map((t) => (
            <span key={t} className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
              {t}
            </span>
          ))}
        </div>

        {site.last_connection_check_at && (
          <p className="text-xs text-muted-foreground">
            Son test: {formatDate(site.last_connection_check_at)}
          </p>
        )}

        <div className="flex items-center gap-1 pt-2 border-t">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => test.mutate()}
            disabled={test.isPending}
            className="flex-1"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', test.isPending && 'animate-spin')} /> Test
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} disabled={del.isPending}>
            <Trash2 className="w-3.5 h-3.5" />
            {confirming ? 'Emin misin?' : ''}
          </Button>
        </div>

        {test.data && (
          <div
            className={cn(
              'text-xs rounded-md px-2 py-1.5',
              test.data.ok
                ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200'
                : 'bg-destructive/10 text-destructive',
            )}
          >
            {test.data.message}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ConnectionBadge({ status }: { status: Site['connection_status'] }) {
  if (status === 'ok') {
    return (
      <span
        title="Bağlantı OK"
        className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs px-2 py-0.5"
      >
        <CheckCircle2 className="w-3 h-3" /> OK
      </span>
    );
  }
  if (status === 'auth_fail' || status === 'unreachable') {
    return (
      <span
        title="Bağlantı hatalı"
        className="inline-flex items-center gap-1 rounded-full bg-destructive/10 text-destructive text-xs px-2 py-0.5"
      >
        <AlertCircle className="w-3 h-3" /> Hata
      </span>
    );
  }
  return (
    <span
      title="Henüz test edilmedi"
      className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground text-xs px-2 py-0.5"
    >
      <HelpCircle className="w-3 h-3" />
    </span>
  );
}
